/**
 * Dialog Operations
 *
 * TypeScript implementation of popup/dialog operations.
 * Replaces legacy functions: showPopup, hidePopup, updatePopup,
 * showKereses, showEllenorzes, showInformacio.
 */

import type { Puzzle } from '../core/Puzzle';
import type { HistoryManager } from '../core/HistoryManager';
import type { AppState } from '../state/AppState';

/** Context for dialog operations */
export interface DialogOperationContext {
    puzzle: Puzzle;
    history: HistoryManager;
    appState: AppState;
    onMenuUpdate: () => void;
    onHistoryUpdate: () => void;
}

/**
 * Show a popup dialog
 */
export function showPopup(content: string): void {
    const popup = document.getElementById('rejtveny_popupmenu');
    if (!popup) return;

    popup.innerHTML = content;
    updatePopupPosition();

    // Hide/show overlay elements
    const zarFixMenu = document.getElementById('zarFixMenu');
    const zarRejtveny = document.getElementById('zarRejtveny');
    const zarPopUp = document.getElementById('zarPopUp');
    const component = document.getElementById('rejtvenyKomponens');

    if (zarFixMenu) zarFixMenu.style.display = 'none';
    if (zarRejtveny) zarRejtveny.style.display = 'none';
    if (zarPopUp) zarPopUp.style.display = 'block';
    if (component) component.style.position = 'static';
}

/**
 * Update popup position to center it
 */
export function updatePopupPosition(): void {
    const popup = document.getElementById('rejtveny_popupmenu');
    const puzzle = document.getElementById('rejtveny');

    if (!popup || !puzzle) return;

    popup.style.display = 'block';
    popup.style.left = `${Math.ceil(
        puzzle.offsetWidth / 2 - popup.offsetWidth / 2,
    )}px`;
    popup.style.top = `${Math.ceil(
        puzzle.offsetHeight / 2 - popup.offsetHeight / 2,
    )}px`;
}

/**
 * Hide the popup dialog
 */
export function hidePopup(appState: AppState): void {
    const popup = document.getElementById('rejtveny_popupmenu');
    if (popup) popup.style.display = 'none';

    // Show/hide overlay elements
    const zarFixMenu = document.getElementById('zarFixMenu');
    const zarRejtveny = document.getElementById('zarRejtveny');
    const zarPopUp = document.getElementById('zarPopUp');
    const component = document.getElementById('rejtvenyKomponens');

    // Only restore overlays if there are unsaved changes
    if (!appState.isSaved) {
        if (zarFixMenu) zarFixMenu.style.display = 'block';
        if (zarRejtveny) zarRejtveny.style.display = 'block';
    }
    if (zarPopUp) zarPopUp.style.display = 'none';
    if (component) component.style.position = 'relative';

    // Focus back on current element
    appState.currentElement?.focus();
}

/**
 * Show information dialog
 */
export function showInformation(ctx: DialogOperationContext): void {
    const content = `<form>
        <div class="input-block" id="informacio">Töltés...</div>
        <div class="input-block"><input type="button" id="infoCloseBtn" value="OK"></div>
    </form>`;

    showPopup(content);

    // Attach close handler
    const closeBtn = document.getElementById('infoCloseBtn');
    if (closeBtn) {
        closeBtn.addEventListener('click', () => hidePopup(ctx.appState));
    }

    // Fetch information
    fetch(`/informacio?rejtvenyID=${ctx.appState.puzzleId}`)
        .then((response) => response.text())
        .then((html) => {
            const infoEl = document.getElementById('informacio');
            if (infoEl) {
                const mode = ctx.appState.isBuilderMode ? 'építő' : 'fejtő';
                infoEl.innerHTML = `${html}<br><br>Üzemmód: ${mode}`;
                updatePopupPosition();
            }
        })
        .catch((error) => {
            console.error('Error fetching information:', error);
            const infoEl = document.getElementById('informacio');
            if (infoEl) infoEl.innerHTML = 'Hiba az információ betöltésekor';
        });
}

/**
 * Get the full selection array for a cell.
 * Like legacy getTeljesTomb: includes current cell and sorts.
 */
function getFullSelection(ctx: DialogOperationContext): number[] {
    const selection = [...ctx.appState.currentSelection];

    // Add current cell if not already in selection (like legacy getTeljesTomb)
    if (!selection.includes(ctx.appState.currentCellId)) {
        selection.push(ctx.appState.currentCellId);
    }

    // Sort by cell ID (like legacy rendezes)
    selection.sort((a, b) => a - b);

    return selection;
}

/**
 * Get expression from selection.
 * Like legacy getKifejezes: uses _ for empty/invalid cells.
 */
function getExpression(
    ctx: DialogOperationContext,
    selection: number[],
): string {
    const regex = new RegExp(ctx.appState.validCharPattern, 'g');

    return selection
        .map((id) => {
            const cell = ctx.puzzle.getCell(id);
            const value = cell?.value;

            // Use _ for empty or invalid characters (like legacy getKifejezes)
            if (value && regex.test(value)) {
                // Reset regex lastIndex for next test
                regex.lastIndex = 0;
                return value;
            }
            regex.lastIndex = 0;
            return '_';
        })
        .join('');
}

/**
 * Show search dialog
 */
export function showSearch(ctx: DialogOperationContext): void {
    // Only available in builder mode
    if (!ctx.appState.isBuilderMode) return;

    const selection = getFullSelection(ctx);
    const expression = getExpression(ctx, selection);

    const content = `<form>
        <div class="input-block" id="keresesSelect">Keresés...</div>
    </form>`;

    showPopup(content);

    // Perform search
    searchExpression(expression, expression, ctx);
}

/**
 * Search for expression in dictionary
 */
function searchExpression(
    pattern: string,
    expression: string,
    ctx: DialogOperationContext,
): void {
    const targetEl = document.getElementById('keresesSelect');
    if (!targetEl) return;

    const params = `minta=${encodeURIComponent(
        pattern,
    )}&kifejezes=${encodeURIComponent(expression)}`;

    fetch('/kereses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params,
    })
        .then((response) => response.text())
        .then((html) => {
            targetEl.innerHTML = html;

            // Add buttons if not present
            if (!document.getElementById('keresesGomb')) {
                const parent = targetEl.parentNode as HTMLElement;
                if (parent) {
                    const buttonsHtml = `</div><div class="input-block" id="keresesGomb">
                        <input type="button" id="searchInsertBtn" value="Beszúrás">
                        <input type="reset" id="searchCancelBtn" value="Mégse">
                    </div>`;
                    parent.insertAdjacentHTML('beforeend', buttonsHtml);

                    // Attach handlers
                    const insertBtn =
                        document.getElementById('searchInsertBtn');
                    const cancelBtn =
                        document.getElementById('searchCancelBtn');

                    if (insertBtn) {
                        insertBtn.addEventListener('click', () =>
                            insertSearchResult(ctx),
                        );
                    }
                    if (cancelBtn) {
                        cancelBtn.addEventListener('click', () =>
                            hidePopup(ctx.appState),
                        );
                    }
                }
            }

            updatePopupPosition();
        })
        .catch((error) => {
            console.error('Search error:', error);
            targetEl.innerHTML = 'Hiba a keresés során';
        });
}

/**
 * Insert search result into puzzle
 */
function insertSearchResult(ctx: DialogOperationContext): void {
    const expressionSelect = document.getElementById(
        'selectKifejezesek',
    ) as HTMLSelectElement;
    const clueSelect = document.getElementById(
        'selectMeghatarozasok',
    ) as HTMLSelectElement;

    if (!expressionSelect || !clueSelect) return;

    const expression =
        expressionSelect.options[expressionSelect.selectedIndex]?.value;
    const clue = clueSelect.options[clueSelect.selectedIndex]?.value;

    if (expression) {
        setExpressionToSelection(ctx, expression);
    }
    if (clue) {
        setClueToSelection(ctx, clue);
    }

    hidePopup(ctx.appState);
    ctx.appState.currentElement?.focus();
}

/**
 * Set expression to current selection
 */
function setExpressionToSelection(
    ctx: DialogOperationContext,
    expression: string,
): void {
    const selection = getFullSelection(ctx);
    const chars = expression.split('');

    for (let i = 0; i < selection.length && i < chars.length; i++) {
        const cell = ctx.puzzle.getCell(selection[i]);
        if (cell && !cell.isBlack && !cell.isImage && !cell.isEmpty) {
            cell.setValue(chars[i]);
            const cellEl = document.getElementById(`mezo-${selection[i]}`);
            if (cellEl) {
                cellEl.innerHTML = chars[i];
            }
        }
    }

    ctx.history.save('Kifejezés beírása');
    ctx.onHistoryUpdate();
}

/**
 * Set clue to first cell in selection.
 * Like legacy setMeghatarozas: also sets expression and updates background image.
 */
function setClueToSelection(ctx: DialogOperationContext, clue: string): void {
    const selection = getFullSelection(ctx);
    if (selection.length === 0) return;

    const firstCellId = selection[0];
    const cell = ctx.puzzle.getCell(firstCellId);
    if (!cell) return;

    // Get expression from selection (like legacy getKifejezes)
    const expression = getExpression(ctx, selection);

    // Note: When editing horizontally (isHorizontal=true), we set VERTICAL clue (vmeghatározás)
    // This matches legacy behavior where g_mezoIrany=true -> setVmeghatarozas
    if (ctx.appState.isHorizontal) {
        cell.setVerticalClue(clue);
        // Also set expression if clue exists (like legacy setVkifejezes)
        if (cell.hasVerticalClue) {
            cell.setVerticalExpression(expression);
        }
    } else {
        cell.setHorizontalClue(clue);
        // Also set expression if clue exists (like legacy setFkifejezes)
        if (cell.hasHorizontalClue) {
            cell.setHorizontalExpression(expression);
        }
    }

    // Update background image on DOM element (like legacy)
    const cellEl = document.getElementById(`mezo-${firstCellId}`);
    if (cellEl) {
        cellEl.style.backgroundImage = cell.getBackgroundImage(
            ctx.appState.isHorizontal,
            !ctx.appState.clueMarking,
        );
    }

    ctx.history.save('Meghatározás beírása');
    ctx.onHistoryUpdate();
}

/**
 * Show verification error popup with appropriate buttons.
 * In builder mode, shows "Véglegesítés" (finalize) button.
 * In solver mode, shows just "OK" button.
 */
function showVerificationErrors(
    ctx: DialogOperationContext,
    errors: string,
): void {
    let html = '<form>';
    html +=
        '<div class="input-block"><b>Az ellenőrzés során felismert hibák:</b>';
    html += errors;
    html += '</div>';
    html += '<div class="input-block">';

    if (ctx.appState.isBuilderMode) {
        html +=
            '<input type="button" id="verifyFinalizeBtn" value="Véglegesítés">';
    } else {
        html += '<input type="button" id="verifyOkBtn" value="OK">';
    }

    html += '<input type="reset" id="verifyCancelBtn" value="Mégse">';
    html += '</div>';
    html += '</form>';

    showPopup(html);

    // Attach event handlers
    const finalizeBtn = document.getElementById('verifyFinalizeBtn');
    const okBtn = document.getElementById('verifyOkBtn');
    const cancelBtn = document.getElementById('verifyCancelBtn');

    if (finalizeBtn) {
        finalizeBtn.addEventListener('click', () => {
            // Force verify despite errors (véglegesEllenorzes)
            ctx.appState.isVerified = true;
            hidePopup(ctx.appState);
            ctx.onMenuUpdate();
        });
    }

    if (okBtn) {
        okBtn.addEventListener('click', () => hidePopup(ctx.appState));
    }

    if (cancelBtn) {
        cancelBtn.addEventListener('click', () => hidePopup(ctx.appState));
    }
}

/**
 * Show verification dialog.
 * Migrated from legacy showEllenorzes function.
 *
 * In builder mode: checks locally for expression errors and missing clues.
 * In solver mode: sends puzzle XML to server for verification.
 */
export function showVerification(ctx: DialogOperationContext): void {
    showPopup('<div class="input-block">Ellenőrzés...</div>');

    if (ctx.appState.isBuilderMode) {
        // Builder mode: local validation
        let errors = '';

        if (ctx.puzzle.hasExpressionErrors()) {
            errors += '<br>- hibás kifejezés, meghatározás párok';
        }

        if (ctx.puzzle.hasClueErrors()) {
            errors += '<br>- hiányzó meghatározások';
        }

        if (errors === '') {
            // No errors - verification passed
            hidePopup(ctx.appState);
            ctx.appState.isVerified = true;
            ctx.onMenuUpdate();
        } else {
            // Show errors
            showVerificationErrors(ctx, errors);
        }
    } else {
        // Solver mode: server validation
        const puzzleXml = ctx.puzzle.toXml();

        fetch(`/ellenorzes?rejtvenyID=${ctx.appState.puzzleId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'text/xml;charset=UTF-8',
            },
            body: puzzleXml,
        })
            .then((response) => {
                if (response.ok) {
                    // Verification passed
                    hidePopup(ctx.appState);
                    ctx.appState.isVerified = true;
                    ctx.onMenuUpdate();
                } else {
                    // Puzzle incomplete / mismatch
                    showVerificationErrors(ctx, '<br>- nem teljes a rejtvény');
                }
            })
            .catch((error) => {
                console.error('Verification error:', error);
                hidePopup(ctx.appState);
            });
    }
}

/**
 * Remove verification status
 */
export function unverify(ctx: DialogOperationContext): void {
    ctx.appState.isVerified = false;
    ctx.onMenuUpdate();
}

/**
 * Show mask dialog with download links, upload, and delete options.
 * Like legacy changeMaszk function.
 */
export function showMaskUpload(ctx: DialogOperationContext): void {
    if (!ctx.appState.allowImages) return;

    const puzzleId = ctx.appState.puzzleId;
    const hasGraphics = ctx.appState.hasGraphics;
    const link = `/maszkletoltes?rejtvenyID=${puzzleId}`;
    const link2 = `${link}&stilus=1`;

    let content = '<form>';

    // Section 1: Download links
    content +=
        '<div class="input-block"><b>1. A rejtvényről készült maszkok:</b>';
    content += `<br>- <a href="${link}" target="_blank">Egyszerű maszk</a>`;

    if (hasGraphics) {
        content += `<br>- <a href="${link2}" target="_blank">Aktuális maszk minta</a>`;
    }

    content += '</div>';

    // Section 2: Upload
    content +=
        '<div class="input-block"><b>2. Egyedi maszk feltöltésének helye:</b><br>' +
        '<input id="maskFile" type="file" name="maskFile" required accept="image/jpeg" /></div>';

    // Progress bar (hidden initially)
    content +=
        '<div class="input-block" id="maskProgressFieldset" style="display:none;">' +
        '<progress id="maskProgressBar" value="0" max="100"></progress>' +
        '<div id="maskPercentage">0%</div></div>';

    // Buttons
    content += '<div class="input-block">';
    content += '<input type="button" id="maskUploadBtn" value="Feltöltés">';
    content += '<input type="reset" id="maskCancelBtn" value="Mégse">';

    if (hasGraphics) {
        content += '<input type="button" id="maskDeleteBtn" value="Törlés">';
    }

    content += '</div>';
    content += '</form>';

    showPopup(content);

    // Attach event handlers
    const uploadBtn = document.getElementById('maskUploadBtn');
    const cancelBtn = document.getElementById('maskCancelBtn');
    const deleteBtn = document.getElementById('maskDeleteBtn');

    if (uploadBtn) {
        uploadBtn.addEventListener('click', () => {
            const fileInput = document.getElementById(
                'maskFile',
            ) as HTMLInputElement;
            if (fileInput?.files?.length) {
                uploadMask(ctx, fileInput.files[0]);
            }
        });
    }

    if (cancelBtn) {
        cancelBtn.addEventListener('click', () => hidePopup(ctx.appState));
    }

    if (deleteBtn) {
        deleteBtn.addEventListener('click', () => deleteMask(ctx));
    }

    // Focus back on current element
    ctx.appState.currentElement?.focus();
}

/**
 * Upload mask image with progress indicator.
 * Like legacy feltoltesMaszk function.
 *
 * Sends raw file data (not FormData) with rejtvenyID query param.
 */
function uploadMask(ctx: DialogOperationContext, file: File): void {
    // Show progress popup
    showPopup(
        ctx.appState,
        '<div class="input-block"><progress id="progressBar" value="0" max="100"></progress>' +
            '<div id="percentageCalc">0%</div></div>',
    );

    const xhr = new XMLHttpRequest();

    // Track upload progress
    xhr.upload.addEventListener('progress', (e) => {
        const progressBar = document.getElementById(
            'progressBar',
        ) as HTMLProgressElement;
        const percentageDiv = document.getElementById('percentageCalc');
        if (e.lengthComputable && progressBar && percentageDiv) {
            const percentage = Math.round((e.loaded / e.total) * 100);
            progressBar.value = percentage;
            progressBar.max = 100;
            percentageDiv.textContent = `${percentage}%`;
        }
    });

    xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
            // Parse XML response to get grafika element
            const parser = new DOMParser();
            const dom = parser.parseFromString(xhr.responseText, 'text/xml');
            const grafika = dom.getElementsByTagName('grafika').item(0);

            if (grafika && ctx.puzzle) {
                ctx.puzzle.setMaskImagesFromXml(grafika);
                ctx.appState.hasGraphics = true;
                ctx.history.save('Maszk feltöltése');
                ctx.onHistoryUpdate();
            }
        }
        hidePopup(ctx.appState);
        ctx.onMenuUpdate();
    });

    xhr.addEventListener('error', () => {
        console.error('Mask upload error');
        hidePopup(ctx.appState);
    });

    xhr.open('POST', `/maszkmentes?rejtvenyID=${ctx.appState.puzzleId}`);
    xhr.send(file);
}

/**
 * Delete mask graphics.
 * Like legacy torlesMaszk function.
 */
function deleteMask(ctx: DialogOperationContext): void {
    ctx.puzzle.clearMaskImages();
    ctx.appState.hasGraphics = false;

    ctx.history.save('Maszk törlése');
    ctx.onHistoryUpdate();
    ctx.onMenuUpdate();

    hidePopup(ctx.appState);
}

/**
 * Show color scheme selector dialog
 * Like legacy changeSzinsema: parses XML and generates radio buttons
 */
export function showColorScheme(ctx: DialogOperationContext): void {
    if (!ctx.appState.isBuilderMode) return;

    const content = `<form>
        <div class="input-block" id="szinsema">Töltés...</div>
        <div class="input-block">
            <input type="button" id="colorApplyBtn" value="OK">
            <input type="reset" id="colorCancelBtn" value="Mégse">
        </div>
    </form>`;

    showPopup(content);

    // Attach button handlers
    const applyBtn = document.getElementById('colorApplyBtn');
    const cancelBtn = document.getElementById('colorCancelBtn');

    if (applyBtn) {
        applyBtn.addEventListener('click', () => applyColorScheme(ctx));
    }
    if (cancelBtn) {
        cancelBtn.addEventListener('click', () => hidePopup(ctx.appState));
    }

    // Fetch available color schemes (returns XML)
    fetch('/szinsemak')
        .then((response) => response.text())
        .then((xmlText) => {
            const selectEl = document.getElementById('szinsema');
            if (!selectEl) return;

            // Parse XML response
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
            const elements = xmlDoc.getElementsByTagName('minta');

            // Get current border color for pre-selection
            const currentBorderColor = ctx.puzzle.borderColor?.toUpperCase();

            // Generate radio buttons for each color scheme
            let html = '';
            for (let i = 0; i < elements.length; i++) {
                const keretszin = elements[i].getAttribute('keretszín') || '';
                const hatterszin = elements[i].getAttribute('háttérszín') || '';
                const betuszin = elements[i].getAttribute('betűszín') || '';

                // Check if this scheme matches current puzzle colors
                const isChecked =
                    currentBorderColor === keretszin.toUpperCase()
                        ? ' checked="checked"'
                        : '';

                // Value format: keretszín-háttérszín-betűszín (like legacy)
                const value =
                    `${keretszin}-${hatterszin}-${betuszin}`.toUpperCase();

                html += `<div class="szinminta">
                    <div class="kulsominta" style="background-color:${keretszin};">
                        <span class="belsominta" style="background-color:${hatterszin};">A</span>
                    </div>
                    <br>
                    <input type="radio" name="szinsema" value="${value}"${isChecked}>
                </div>`;
            }

            selectEl.innerHTML = html;
            updatePopupPosition();
        })
        .catch((error) => {
            console.error('Error fetching color schemes:', error);
            const selectEl = document.getElementById('szinsema');
            if (selectEl) {
                selectEl.innerHTML = 'Hiba a színsémák betöltésekor';
            }
        });
}

/**
 * Apply selected color scheme
 * Like legacy setSzinsema: gets radio value and applies colors directly
 */
function applyColorScheme(ctx: DialogOperationContext): void {
    // Find selected radio button
    const radios = document.getElementsByName(
        'szinsema',
    ) as NodeListOf<HTMLInputElement>;
    let selectedValue = '';

    for (const radio of radios) {
        if (radio.checked) {
            selectedValue = radio.value;
            break;
        }
    }

    if (!selectedValue) {
        hidePopup(ctx.appState);
        return;
    }

    // Parse the value: keretszín-háttérszín-betűszín
    const colors = selectedValue.split('-');
    if (colors.length !== 3) {
        hidePopup(ctx.appState);
        return;
    }

    // Apply colors to puzzle
    ctx.puzzle.setColors(colors[0], colors[1], colors[2]);

    // Update visual appearance
    applyColorsToDOM(ctx.puzzle);

    ctx.history.save('Színséma kiválasztása');
    ctx.onHistoryUpdate();
    hidePopup(ctx.appState);
    ctx.onMenuUpdate();
}

/**
 * Apply puzzle colors to DOM elements
 */
function applyColorsToDOM(puzzle: Puzzle): void {
    // Apply to numbered cells
    const numberedCells = document.getElementsByClassName('számosmező');
    for (let i = 0; i < numberedCells.length; i++) {
        const cell = numberedCells[i] as HTMLElement;
        cell.style.backgroundColor = puzzle.borderColor;
        cell.style.borderColor = puzzle.borderColor;
        cell.style.color = puzzle.textColor;
    }

    // Apply to colored cells
    const coloredCells = document.getElementsByClassName('színes');
    for (let i = 0; i < coloredCells.length; i++) {
        const cell = coloredCells[i] as HTMLElement;
        cell.style.backgroundColor = puzzle.backgroundColor;
    }
}
